import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import HeroCanvas from "@/components/hero/HeroCanvas";
import GlowButton from "@/components/primitives/GlowButton";
import GhostLink from "@/components/primitives/GhostLink";

const fadeUp = {
    hidden: { opacity: 0, y: 22, filter: "blur(8px)" },
    show: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export default function Hero() {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative min-h-screen w-full overflow-hidden bg-tca-deep flex items-center"
        >
            {/* R3F canvas (or static SVG fallback) */}
            <div className="absolute inset-0 z-0" data-testid="hero-canvas-wrap">
                <HeroCanvas />
            </div>

            {/* HUD grid overlay */}
            <div aria-hidden="true" className="absolute inset-0 z-[1] tca-grid-bg opacity-60" />
            <div aria-hidden="true" className="absolute inset-0 z-[2] tca-vignette" />

            {/* Side rails (HUD-style numeric ticks) */}
            <div
                aria-hidden="true"
                className="hidden lg:flex absolute left-6 top-1/2 -translate-y-1/2 z-[3] flex-col gap-3 font-mono-tight text-[10px] tracking-[0.3em] text-tca-ink-dim/80"
            >
                <span className="text-tca-cyan/70">QC.OS</span>
                <span>v1.0.0</span>
                <span className="opacity-60">A→H</span>
            </div>
            <div
                aria-hidden="true"
                className="hidden lg:flex absolute right-6 top-1/2 -translate-y-1/2 z-[3] flex-col gap-3 font-mono-tight text-[10px] tracking-[0.3em] text-tca-ink-dim/80 items-end"
            >
                <span>SECTOR · 01</span>
                <span className="text-tca-cyan/70">[ ONLINE ]</span>
                <span className="opacity-60">UPLINK · OK</span>
            </div>

            {/* Content */}
            <div className="relative z-[5] w-full max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 pt-28 sm:pt-32 pb-24 sm:pb-32">
                <motion.div initial="hidden" animate="show" variants={fadeUp} className="max-w-3xl">
                    {/* Eyebrow */}
                    <motion.div
                        variants={fadeUp}
                        className="font-mono-tight text-[10px] sm:text-[11px] uppercase tracking-[0.36em] text-tca-cyan/85 mb-6 sm:mb-8 flex items-center gap-3"
                        data-testid="hero-eyebrow"
                    >
                        <span className="inline-block h-px w-8 bg-tca-cyan/50" />
                        The A–Z System · A through H deployable
                    </motion.div>

                    {/* Display headline */}
                    <motion.h1
                        variants={fadeUp}
                        data-testid="hero-headline"
                        className="font-display text-[44px] leading-[1.02] sm:text-[68px] sm:leading-[0.98] lg:text-[88px] lg:leading-[0.96] tracking-[-0.025em] text-tca-ink"
                    >
                        Build AI Systems
                        <br />
                        <span className="italic neon-cyan font-light">Correctly.</span>
                    </motion.h1>

                    {/* Mono terminal sub */}
                    <motion.div
                        variants={fadeUp}
                        data-testid="hero-mono-sub"
                        className="mt-7 sm:mt-9 font-mono-tight text-[12px] sm:text-sm text-tca-ink-muted flex items-center gap-3"
                    >
                        <span className="text-tca-cyan/80">$</span>
                        <span className="mono-cursor">Engineered on QUADRA Core® OS</span>
                    </motion.div>

                    {/* Body sub */}
                    <motion.p
                        variants={fadeUp}
                        data-testid="hero-tagline"
                        className="mt-6 sm:mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-tca-ink-muted"
                    >
                        Stop guessing. Start building. The structured A–Z system for
                        engineering intelligent agents — no templates, no hand-waving,
                        no fragmented stacks.
                    </motion.p>

                    {/* CTAs */}
                    <motion.div
                        variants={fadeUp}
                        className="mt-10 sm:mt-12 flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-7"
                        data-testid="hero-cta-row"
                    >
                        <GlowButton
                            size="md"
                            as="a"
                            href="#access"
                            data-testid="hero-cta-primary"
                        >
                            Get the System
                        </GlowButton>
                        <GhostLink
                            href="#system"
                            data-testid="hero-cta-secondary"
                        >
                            Explore the A–Z System
                        </GhostLink>
                    </motion.div>
                </motion.div>
            </div>

            {/* Scroll cue */}
            <motion.a
                href="#trust"
                data-testid="hero-scroll-cue"
                aria-label="Scroll to next section"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 0.6 }}
                className="absolute z-[6] left-1/2 -translate-x-1/2 bottom-7 sm:bottom-9 flex flex-col items-center gap-2 text-tca-ink-muted hover:text-tca-cyan transition-colors"
            >
                <span className="font-mono-tight text-[10px] uppercase tracking-[0.32em]">scroll</span>
                <motion.span
                    animate={{ y: [0, 6, 0] }}
                    transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                    className="inline-block"
                >
                    <ChevronDown size={18} />
                </motion.span>
            </motion.a>
        </section>
    );
}
